import React, { useRef } from 'react';
import moment from 'moment/moment';
import Modal from '@/components/ui/Modal';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { ClassroomSchema } from '@/types/api/v2/ClassroomSchema';
import AddProgramDropdown from './AddProgramDropdown';
import LinkedProgramsTable from './LinkedProgramsTable';
import SaveErrorBanner, { FailedProgram } from './SaveErrorBanner';
import useAvailablePrograms from './useAvailablePrograms';
import useLinkedPrograms from './useLinkedPrograms';
import useManageProgramsSave, { ProgramFailure } from './useManageProgramsSave';
import useProgramSelection from './useProgramSelection';

type RowData = ClassroomSchema;

interface ManageProgramsModalProps {
  closeModal: () => void;
  isOpen: boolean;
  onSaveSuccess?: () => void;
  rowData: RowData;
  subscriberId: string;
}

const formatDate = (date: string) => moment(date).format('MMM DD, YYYY');

const resolveFailedPrograms = (
  failures: ProgramFailure[],
  nameLookup: Record<number, string>,
): FailedProgram[] => failures
  .filter(f => f.programId === undefined || nameLookup[f.programId] !== undefined)
  .map((failure) => {
    const name = (failure.programId !== undefined && nameLookup[failure.programId])
      || 'A program';
    return { id: failure.programId, message: failure.message, name };
  });

export const ManageProgramsModal = ({
  closeModal,
  isOpen,
  onSaveSuccess,
  rowData,
  subscriberId,
}: ManageProgramsModalProps) => {
  const academicTerm = rowData.term_start_date && rowData.term_end_date
    ? `${formatDate(rowData.term_start_date)} – ${formatDate(rowData.term_end_date)}`
    : null;

  const {
    error: linkedError,
    isLoading: linkedLoading,
    programs: linkedPrograms,
    refetch: refetchLinkedPrograms,
  } = useLinkedPrograms(rowData.id);

  const {
    availablePrograms,
    error: availableError,
    isLoading: availableLoading,
  } = useAvailablePrograms(subscriberId);

  const {
    editCounter,
    handleAddProgram,
    handleRemoveStaged,
    handleToggleUnlink,
    hasChanges,
    linkedProgramIds,
    localPrograms,
    programsToLink,
    programsToUnlink,
  } = useProgramSelection(linkedPrograms);

  const namesAtSaveRef = useRef<Record<number, string>>({});

  const { isSaving, programFailures, save, saveError } = useManageProgramsSave({
    classroomId: rowData.id,
    classroomUid: rowData.uid,
    editCounter,
    onPartialFailure: refetchLinkedPrograms,
    onSuccess: () => {
      onSaveSuccess?.();
      closeModal();
    },
    programsToLink,
    programsToUnlink,
  });

  const handleSave = () => {
    if (!hasChanges) {
      closeModal();
      return;
    }
    namesAtSaveRef.current = Object.fromEntries(localPrograms.map(p => [p.id, p.name]));
    save();
  };

  const failedPrograms = resolveFailedPrograms(programFailures, namesAtSaveRef.current);

  return (
    <Modal
      bodyClassName="!tw-overflow-y-hidden tw-flex tw-flex-col"
      className="tw-flex tw-flex-col !tw-w-[950px]"
      close={closeModal}
      headerText="Manage Programs"
      isOpen={isOpen}
    >
      <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0">
        {saveError && <SaveErrorBanner failedPrograms={failedPrograms} kind={saveError} />}

        <div className="tw-flex tw-flex-col tw-gap-2 tw-pb-4 tw-border-b tw-border-solid tw-border-med-grey">
          <p>
            <strong>Class UID:</strong>
            {' '}{rowData.uid}
          </p>
          <p>
            <strong>Class Name:</strong>
            {' '}{rowData.full_name}
          </p>
          <p>
            <strong>Teacher(s):</strong>
            {' '}{rowData.teachers}
          </p>
          {academicTerm && (
            <p>
              <strong>Academic Term:</strong>
              {' '}{academicTerm}
            </p>
          )}
        </div>

        <AddProgramDropdown
          availablePrograms={availablePrograms}
          isDisabled={isSaving}
          isLoading={availableLoading}
          linkedProgramIds={linkedProgramIds}
          onSelect={handleAddProgram}
        />

        {availableError && <div className="tw-text-red tw-py-2">{availableError}</div>}

        {linkedLoading && (
          <div className="tw-text-center tw-py-4">
            <i aria-label="Loading" className="fa fa-spinner fa-spin fa-2x" />
          </div>
        )}

        {linkedError && <div className="tw-text-red tw-py-2">{linkedError}</div>}

        {!linkedLoading && !linkedError && (
          <LinkedProgramsTable
            disabled={isSaving}
            programs={localPrograms}
            programsToUnlink={programsToUnlink}
            onRemoveStaged={handleRemoveStaged}
            onToggleUnlink={handleToggleUnlink}
          />
        )}
      </div>

      <div className="tw-flex-shrink-0">
        <Modal.SubmitFooter
          close={closeModal}
          isLoading={isSaving}
          isValid={hasChanges && !isSaving && !saveError}
          submitText="Save"
          submittingText="Saving..."
          onSubmit={handleSave}
        />
      </div>
    </Modal>
  );
};

const ManageProgramsModalWrapper = (props: ManageProgramsModalProps) => (
  <ReactQueryProvider>
    <ManageProgramsModal {...props} />
  </ReactQueryProvider>
);

export default ManageProgramsModalWrapper;
